import React, { useEffect, useMemo, useState } from 'react';
import { useRoomBuilder } from './hooks/useRoomBuilder';
import { Toolbar } from './components/Toolbar';
import { Canvas } from './components/Canvas';
import { ViewControls } from './components/ViewControls';
import { TemplateDrawer } from './components/TemplateDrawer';
import { SoffitTemplateDrawer } from './components/SoffitTemplateDrawer';
import { CanvasControls } from './components/CanvasControls';
import { Point, Wall, Soffit } from './types/room';
import { motion } from 'framer-motion';
import { Undo, Redo, Trash2, ChevronRight } from 'lucide-react';
import { generateRectangle, generateLShape, generateUShape, generateGalley, generateLShapeClosed, generateUShapeClosed, generateGalleyClosed, generateHalfTrapezoid, generateTriangle, getConnectedWallUpdates } from './utils/geometry';
export function App() {
  const {
    roomData,
    activeTool,
    setActiveTool,
    viewMode,
    setViewMode,
    selectedObjectId,
    setSelectedObjectId,
    isDrawing,
    setIsDrawing,
    drawingPoints,
    setDrawingPoints,
    isTemplateDrawerOpen,
    setIsTemplateDrawerOpen,
    selectedTemplate,
    setSelectedTemplate,
    isPlacingTemplate,
    setIsPlacingTemplate,
    templateStart,
    setTemplateStart,
    templateEnd,
    setTemplateEnd,
    zoom,
    pan,
    sunRadius,
    setSunRadius,
    sunCenter,
    setSunCenter,
    isDrawingSoffit,
    setIsDrawingSoffit,
    soffitPoints,
    setSoffitPoints,
    selectedSoffitTemplate,
    setSelectedSoffitTemplate,
    isPlacingSoffitTemplate,
    setIsPlacingSoffitTemplate,
    soffitTemplateStart,
    setSoffitTemplateStart,
    isDraggingObstacle,
    setIsDraggingObstacle,
    draggedObstacleId,
    setDraggedObstacleId,
    showGuideLines,
    setShowGuideLines,
    isRotatingRoom,
    setIsRotatingRoom,
    isMovingRoom,
    setIsMovingRoom,
    roomRotation,
    addWall,
    addWalls,
    addOpening,
    addSoffit,
    addObstacle,
    updateObject,
    deleteObject,
    setSunPosition,
    zoomIn,
    zoomOut,
    resetView,
    rotateRoom,
    moveRoom,
    applyRoomRotation,
    setPan,
    setZoom
  } = useRoomBuilder();
  const [isSoffitTemplateDrawerOpen, setIsSoffitTemplateDrawerOpen] = useState(false);
  // Calculate room area
  const roomArea = useMemo(() => {
    if (roomData.walls.length < 3) return 0;
    // Simple polygon area calculation using shoelace formula
    const points: Point[] = [];
    roomData.walls.forEach(wall => {
      if (!points.some(p => p.x === wall.start.x && p.y === wall.start.y)) {
        points.push(wall.start);
      }
    });
    if (points.length < 3) return 0;
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    return Math.abs(area / 2) / 10000; // Convert to square meters
  }, [roomData.walls]);
  const isRoomClosed = roomData.walls.length >= 3;
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'v' || e.key === 'V') setActiveTool('select');
      if (e.key === 'w' || e.key === 'W') setActiveTool('wall');
      if (e.key === 'o' || e.key === 'O') setActiveTool('window');
      if (e.key === 'd' || e.key === 'D') setActiveTool('door');
      if (e.key === 's' || e.key === 'S') setActiveTool('soffit');
      if (e.key === 'b' || e.key === 'B') setActiveTool('obstacle');
      if (e.key === 'g' || e.key === 'G') setShowGuideLines(!showGuideLines);
      if (e.key === 'Escape') {
        setIsDrawing(false);
        setDrawingPoints([]);
        setIsPlacingTemplate(false);
        setTemplateStart(null);
        setTemplateEnd(null);
        setIsDrawingSoffit(false);
        setSoffitPoints([]);
        setIsPlacingSoffitTemplate(false);
        setSoffitTemplateStart(null);
        setIsDraggingObstacle(false);
        setDraggedObstacleId(null);
        setActiveTool('select');
      }
      if (e.key === 'Delete' && selectedObjectId) {
        deleteObject(selectedObjectId);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedObjectId, showGuideLines, setActiveTool, setIsDrawing, setDrawingPoints, setIsPlacingTemplate, setTemplateStart, setTemplateEnd, setIsDrawingSoffit, setSoffitPoints, setIsPlacingSoffitTemplate, setSoffitTemplateStart, setIsDraggingObstacle, setDraggedObstacleId, setShowGuideLines, deleteObject]);
  const handleStartDrawing = (point: Point) => {
    if (activeTool === 'wall') {
      if (selectedTemplate !== 'free') {
        if (!isPlacingTemplate) {
          setIsPlacingTemplate(true);
          setTemplateStart(point);
        }
      } else {
        if (!isDrawing) {
          setIsDrawing(true);
          setDrawingPoints([point]);
        } else {
          setDrawingPoints([...drawingPoints, point]);
        }
      }
    } else if (activeTool === 'soffit') {
      if (selectedSoffitTemplate !== 'free') {
        if (!isPlacingSoffitTemplate) {
          setIsPlacingSoffitTemplate(true);
          setSoffitTemplateStart(point);
        }
      } else {
        if (!isDrawingSoffit) {
          setIsDrawingSoffit(true);
          setSoffitPoints([point]);
        } else {
          setSoffitPoints([...soffitPoints, point]);
        }
      }
    }
  };
  const handleContinueDrawing = (point: Point) => {};
  const handleTemplatePreview = (start: Point, end: Point): Point[] => {
    switch (selectedTemplate) {
      case 'rectangle':
        return generateRectangle(start, end);
      case 'l-shape':
        return generateLShape(start, end);
      case 'u-shape':
        return generateUShape(start, end);
      case 'galley':
        return generateGalley(start, end);
      default:
        return [];
    }
  };
  const handleSoffitTemplatePreview = (start: Point, end: Point): Point[] => {
    switch (selectedSoffitTemplate) {
      case 'rectangle':
        return generateRectangle(start, end);
      case 'l-shape':
        return generateLShapeClosed(start, end);
      case 'u-shape':
        return generateUShapeClosed(start, end);
      case 'galley':
        return generateGalleyClosed(start, end);
      default:
        return [];
    }
  };
  const handleFinishDrawing = (endPoint: Point) => {
    if (activeTool === 'wall') {
      if (selectedTemplate !== 'free' && isPlacingTemplate && templateStart) {
        const points = handleTemplatePreview(templateStart, endPoint);
        if (points.length > 0) {
          const newWalls: Wall[] = [];
          for (let i = 0; i < points.length - 1; i++) {
            const start = points[i];
            const end = points[i + 1];
            newWalls.push({
              id: `wall-${Date.now()}-${i}`,
              start,
              end,
              thickness: 20
            });
          }
          addWalls(newWalls);
        }
        setIsPlacingTemplate(false);
        setTemplateStart(null);
        setTemplateEnd(null);
        setActiveTool('select');
      } else if (selectedTemplate === 'free' && drawingPoints.length >= 2) {
        const newWalls: Wall[] = [];
        for (let i = 0; i < drawingPoints.length - 1; i++) {
          const start = drawingPoints[i];
          const end = drawingPoints[i + 1];
          newWalls.push({
            id: `wall-${Date.now()}-${i}`,
            start,
            end,
            thickness: 20
          });
        }
        addWalls(newWalls);
        setIsDrawing(false);
        setDrawingPoints([]);
      }
    } else if (activeTool === 'soffit') {
      if (selectedSoffitTemplate !== 'free' && isPlacingSoffitTemplate && soffitTemplateStart) {
        const points = handleSoffitTemplatePreview(soffitTemplateStart, endPoint);
        if (points.length >= 3) {
          const newSoffitId = `soffit-${Date.now()}`;
          const soffit: Soffit = {
            id: newSoffitId,
            points,
            drop: 30,
            depth: 20
          };
          addSoffit(soffit);
          setIsPlacingSoffitTemplate(false);
          setSoffitTemplateStart(null);
          setActiveTool('select');
          // Select immediately - the state should be ready
          setSelectedObjectId(newSoffitId);
        }
      } else if (selectedSoffitTemplate === 'free' && soffitPoints.length >= 3) {
        const newSoffitId = `soffit-${Date.now()}`;
        const soffit: Soffit = {
          id: newSoffitId,
          points: soffitPoints,
          drop: 30,
          depth: 20
        };
        addSoffit(soffit);
        setIsDrawingSoffit(false);
        setSoffitPoints([]);
        setActiveTool('select');
        // Select immediately - the state should be ready
        setSelectedObjectId(newSoffitId);
      }
    }
  };
  const handleUpdateWallWithConnections = (wallId: string, updates: Partial<Wall>) => {
    const wall = roomData.walls.find(w => w.id === wallId);
    if (!wall) return;
    updateObject(wallId, updates);
    if (updates.start) {
      const connectedUpdates = getConnectedWallUpdates(wall.start, updates.start, roomData.walls, wallId);
      connectedUpdates.forEach(({
        id,
        updates: connectedUpdate
      }) => {
        updateObject(id, connectedUpdate);
      });
    }
    if (updates.end) {
      const connectedUpdates = getConnectedWallUpdates(wall.end, updates.end, roomData.walls, wallId);
      connectedUpdates.forEach(({
        id,
        updates: connectedUpdate
      }) => {
        updateObject(id, connectedUpdate);
      });
    }
  };
  const handleMoveAllWalls = (offset: Point) => {
    roomData.walls.forEach(wall => {
      updateObject(wall.id, {
        start: {
          x: wall.start.x + offset.x,
          y: wall.start.y + offset.y
        },
        end: {
          x: wall.end.x + offset.x,
          y: wall.end.y + offset.y
        }
      });
    });
  };
  const handleWallToolClick = () => {
    setIsTemplateDrawerOpen(!isTemplateDrawerOpen);
  };
  const handleSoffitToolClick = () => {
    setIsSoffitTemplateDrawerOpen(!isSoffitTemplateDrawerOpen);
  };
  const handleRotateStart = () => {
    setIsRotatingRoom(!isRotatingRoom);
    setIsMovingRoom(false);
  };
  const handleMoveStart = () => {
    setIsMovingRoom(!isMovingRoom);
    setIsRotatingRoom(false);
  };
  const getSelectedObject = () => {
    if (!selectedObjectId) return {
      object: null,
      type: null
    };
    const wall = roomData.walls.find(w => w.id === selectedObjectId);
    if (wall) return {
      object: null,
      type: null
    };
    const opening = roomData.openings.find(o => o.id === selectedObjectId);
    if (opening) return {
      object: opening,
      type: 'opening' as const
    };
    const soffit = roomData.ceiling.soffits.find(s => s.id === selectedObjectId);
    if (soffit) return {
      object: soffit,
      type: 'soffit' as const
    };
    const obstacle = roomData.obstacles.find(o => o.id === selectedObjectId);
    if (obstacle) return {
      object: obstacle,
      type: 'obstacle' as const
    };
    return {
      object: null,
      type: null
    };
  };
  const {
    object: selectedObject,
    type: selectedObjectType
  } = getSelectedObject();
  return <div className="h-screen w-screen flex flex-col bg-[#2D2D2D] text-white overflow-hidden" data-id="element-0">
      {/* Top Bar - CADVIX Style */}
      <div className="h-16 bg-[#2D2D2D] border-b border-[#4A4A4A] flex items-center justify-between px-6" data-id="element-1">
        <div className="flex items-center gap-8" data-id="element-2">
          <div data-id="element-3">
            <div className="text-[#4FC3F7] font-semibold text-xl tracking-wide" data-id="element-4">
              CADVIX
            </div>
            <div className="text-[#808080] text-xs" data-id="element-5">Room Builder</div>
          </div>

          {/* Project Info - NEW */}
          <div className="flex items-center gap-6 pl-6 border-l border-[#4A4A4A]" data-id="element-6">
            <div data-id="element-7">
              <div className="text-[#808080] text-[10px] font-medium tracking-wider mb-0.5" data-id="element-8">
                CLIENT
              </div>
              <div className="text-white text-sm font-semibold" data-id="element-9">
                Sarah Johnson
              </div>
            </div>
            <div data-id="element-10">
              <div className="text-[#808080] text-[10px] font-medium tracking-wider mb-0.5" data-id="element-11">
                PROJECT
              </div>
              <div className="text-white text-sm font-semibold" data-id="element-12">
                Modern Kitchen Remodel
              </div>
            </div>
            <div data-id="element-13">
              <div className="text-[#808080] text-[10px] font-medium tracking-wider mb-0.5" data-id="element-14">
                PLAN #
              </div>
              <div className="text-[#4FC3F7] text-sm font-bold font-mono" data-id="element-15">
                KR-2024-0847
              </div>
            </div>
          </div>
        </div>

        <ViewControls viewMode={viewMode} onViewModeChange={setViewMode} data-id="element-16" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden" data-id="element-17">
        <Toolbar activeTool={activeTool} onToolChange={setActiveTool} onWallToolClick={handleWallToolClick} onSoffitToolClick={handleSoffitToolClick} selectedObject={selectedObject} selectedObjectType={selectedObjectType} selectedTemplate={selectedTemplate} onSelectTemplate={setSelectedTemplate} selectedSoffitTemplate={selectedSoffitTemplate} onSelectSoffitTemplate={setSelectedSoffitTemplate} onUpdateObject={updates => {
        if (selectedObjectId) {
          updateObject(selectedObjectId, updates);
        }
      }} onDuplicateObject={() => {
        if (!selectedObjectId || !selectedObject) return;
        const opening = roomData.openings.find(o => o.id === selectedObjectId);
        const obstacle = roomData.obstacles.find(o => o.id === selectedObjectId);
        const soffit = roomData.ceiling.soffits.find(s => s.id === selectedObjectId);
        if (opening) {
          const newOpening = {
            ...opening,
            id: `opening-${Date.now()}`,
            position: opening.position + 50
          };
          addOpening(newOpening);
          setSelectedObjectId(newOpening.id);
        } else if (obstacle) {
          const newObstacle = {
            ...obstacle,
            id: `obstacle-${Date.now()}`,
            position: {
              x: obstacle.position.x + 50,
              y: obstacle.position.y + 50
            }
          };
          addObstacle(newObstacle);
          setSelectedObjectId(newObstacle.id);
        } else if (soffit) {
          const newSoffit = {
            ...soffit,
            id: `soffit-${Date.now()}`,
            points: soffit.points.map(p => ({
              x: p.x + 50,
              y: p.y + 50
            }))
          };
          addSoffit(newSoffit);
          setSelectedObjectId(newSoffit.id);
        }
      }} onDeleteObject={() => {
        if (selectedObjectId) {
          deleteObject(selectedObjectId);
          setSelectedObjectId(null);
        }
      }} onDeselectObject={() => setSelectedObjectId(null)} data-id="element-18" />

        <TemplateDrawer isOpen={isTemplateDrawerOpen} selectedTemplate={selectedTemplate} onSelectTemplate={template => {
        setSelectedTemplate(template);
        setIsTemplateDrawerOpen(false);
      }} data-id="element-19" />

        <SoffitTemplateDrawer isOpen={isSoffitTemplateDrawerOpen} selectedTemplate={selectedSoffitTemplate} onSelectTemplate={template => {
        setSelectedSoffitTemplate(template);
        setIsSoffitTemplateDrawerOpen(false);
      }} onClose={() => setIsSoffitTemplateDrawerOpen(false)} data-id="element-20" />

        <div className="flex-1 relative" data-id="element-21">
          <Canvas walls={roomData.walls} openings={roomData.openings} soffits={roomData.ceiling.soffits} obstacles={roomData.obstacles} activeTool={activeTool} isDrawing={isDrawing} drawingPoints={drawingPoints} isDrawingSoffit={isDrawingSoffit} soffitPoints={soffitPoints} selectedObjectId={selectedObjectId} selectedTemplate={selectedTemplate} selectedSoffitTemplate={selectedSoffitTemplate} isPlacingTemplate={isPlacingTemplate} isPlacingSoffitTemplate={isPlacingSoffitTemplate} templateStart={templateStart} soffitTemplateStart={soffitTemplateStart} templateEnd={templateEnd} zoom={zoom} pan={pan} sunPosition={roomData.sunPosition} sunRadius={sunRadius} sunCenter={sunCenter} isDraggingObstacle={isDraggingObstacle} draggedObstacleId={draggedObstacleId} showGuideLines={showGuideLines} isRotatingRoom={isRotatingRoom} isMovingRoom={isMovingRoom} roomRotation={roomRotation} viewMode={viewMode} roomHeight={roomData.height} onStartDrawing={handleStartDrawing} onContinueDrawing={handleContinueDrawing} onFinishDrawing={handleFinishDrawing} onSelectObject={setSelectedObjectId} onUpdateWall={handleUpdateWallWithConnections} onMoveAllWalls={handleMoveAllWalls} onAddOpening={addOpening} onAddObstacle={addObstacle} onAddSoffit={addSoffit} onUpdateObstacle={updateObject} onDeleteObject={deleteObject} onSetDraggingObstacle={setIsDraggingObstacle} onSetDraggedObstacleId={setDraggedObstacleId} onSunPositionChange={setSunPosition} onSunRadiusChange={setSunRadius} onSunCenterChange={setSunCenter} onPanChange={setPan} onZoomChange={setZoom} onTemplatePreview={handleTemplatePreview} onSoffitTemplatePreview={handleSoffitTemplatePreview} onApplyRoomRotation={applyRoomRotation} onMoveRoom={moveRoom} onSetRotatingRoom={setIsRotatingRoom} onSetMovingRoom={setIsMovingRoom} data-id="element-22" />

          {showGuideLines && <div className="absolute top-6 right-6 bg-[#10b981] text-white px-3 py-2 rounded-lg font-mono text-xs font-semibold shadow-lg pointer-events-none z-10" data-id="element-23">
              📏 Guide Lines ON (G)
            </div>}

          <CanvasControls zoom={zoom} onZoomIn={zoomIn} onZoomOut={zoomOut} data-id="element-24" />
        </div>
      </div>

      {/* Bottom Bar - CADVIX Style */}
      <div className="h-16 bg-[#2D2D2D] border-t border-[#4A4A4A] flex items-center justify-between px-6" data-id="element-25">
        <div className="flex items-center gap-3" data-id="element-26">
          {/* Undo/Redo */}
          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="w-10 h-10 flex items-center justify-center text-[#B0B0B0] hover:text-white hover:bg-[#3A3A3A] rounded transition-colors" title="Undo (Ctrl+Z)" data-id="element-27">
            <Undo size={18} data-id="element-28" />
          </motion.button>

          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="w-10 h-10 flex items-center justify-center text-[#B0B0B0] hover:text-white hover:bg-[#3A3A3A] rounded transition-colors" title="Redo (Ctrl+Y)" data-id="element-29">
            <Redo size={18} data-id="element-30" />
          </motion.button>

          <div className="w-px h-8 bg-[#4A4A4A] mx-2" data-id="element-31" />

          {/* Clear All */}
          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={() => {
          // Clear all walls, openings, obstacles, and soffits
          roomData.walls.forEach(wall => deleteObject(wall.id));
          roomData.openings.forEach(opening => deleteObject(opening.id));
          roomData.obstacles.forEach(obstacle => deleteObject(obstacle.id));
          roomData.ceiling.soffits.forEach(soffit => deleteObject(soffit.id));
          // Reset drawing state
          setIsDrawing(false);
          setDrawingPoints([]);
          setIsPlacingTemplate(false);
          setTemplateStart(null);
          setIsDrawingSoffit(false);
          setSoffitPoints([]);
          setSelectedObjectId(null);
        }} className="flex items-center gap-2 px-3 h-10 text-[#B0B0B0] hover:text-white hover:bg-[#3A3A3A] rounded transition-colors" title="Clear All" data-id="element-32">
            <Trash2 size={16} data-id="element-33" />
            <span className="text-xs font-medium" data-id="element-34">Clear All</span>
          </motion.button>
        </div>

        {/* Area Display */}
        <div className="flex items-center gap-6" data-id="element-35">
          <div className="text-right" data-id="element-36">
            <div className="text-[#808080] text-xs font-medium" data-id="element-37">TOTAL AREA</div>
            <div className="text-white text-lg font-semibold" data-id="element-38">
              {roomArea.toFixed(2)} m²
            </div>
          </div>

          <div className="w-px h-10 bg-[#4A4A4A]" data-id="element-39" />

          {/* Next Button - CADVIX Cyan */}
          <motion.button whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} disabled={!isRoomClosed} className={`
              px-6 h-10 rounded-lg font-medium text-sm
              flex items-center gap-2 transition-all
              ${isRoomClosed ? 'bg-[#4FC3F7] text-white hover:bg-[#29B6F6] shadow-lg shadow-cyan-500/20' : 'bg-[#3A3A3A] text-[#808080] cursor-not-allowed'}
            `} data-id="element-40">
            Continue
            <ChevronRight size={16} data-id="element-41" />
          </motion.button>
        </div>
      </div>
    </div>;
}